import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  Modal,
  IconButton,
  Button,
  Typography,
  Tooltip,
  Box,
  Popper,
  Fade,
  Paper,
  List,
  ListItem,
  ListItemText,
  Popover,
} from "@mui/material";
import CustomAccordianV2 from "../../components/dms_common/CustomAccordianV2";
import CustomGenericModal from "../../components/dms_common/CustomGenericModal";
import FormHeader from "../../components/dms_common/FormHeader";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import SearchV2 from "../../components/dms_common/SearchV2";
import {
  GenericModal,
  getGenericModalProps,
  initialGenericModalProps,
} from "../viewOrder/ViewOrderUtils";
import {
  ViewPrimaryReturnConfig,
  ViewPrimaryReturnExpandedRowConfig,
  ViewPrimaryReturnMetadata,
  ViewPrimaryReturnWithoutGRNConfig,
  ViewPrimaryReturnWithoutGRNMetadata,
} from "./ViewPrimaryReturnConfig";
import "./EditPrimaryReturn.css";
import BorderHeading from "../../components/heading/BorderHeading";
import { Loader } from "../../components/loader/Loader";
import CustomTablewithTotalInfoV2 from "../../components/dms_common/CustomTablewithTotalInfoV2";
import {
  getInvoiceDetails,
  getReturnAndReference,
} from "../../services/manageOrderService";
import {
  BatchDetails,
  generateReturnRows,
  generateViewReturnRows,
  generateViewReturnWithoutGRNRows,
  getPayload,
  SkuData,
  ViewReturnBatchDetailsWithoutGRN,
  ViewReturnSkuData,
} from "./PrimaryReturnUtils";
import PrimaryReturnSelect from "./PrimaryReturnSelect";
import { getLabel } from "./PrimaryReturnEdit";
import {
  filterData,
  searchWithStatusFilter,
} from "../../components/dms_common/DmsCommonServices";
const ViewPrimaryReturnWithoutGRN = () => {
  const ConfigDictionary = JSON.parse(
    localStorage.getItem("ConfigDictionary") ?? "{}"
  );
  const navigate = useNavigate();
  const location = useLocation();
  const {
    caseUnitLabelPrimaryReturn,
    packUnitLabelPrimaryReturn,
    otherUnitLabelPrimaryReturn,
  } = ConfigDictionary;
  const currency = ConfigDictionary?.currency ?? "";

  const invoiceNumber = location?.state?.invoiceNumber;

  // For without grn there will be no reference invoice
  // const referenceNumber = location?.state?.referenceNumber;

  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
    initialGenericModalProps
  );

  const [searchedValue, setSearchedValue] = useState<string>("");

  const [loader, setLoader] = useState(false);

  const [rows, setRows] = useState<ViewReturnSkuData[]>([]);
  const [filteredRows, setFilteredRows] = useState<ViewReturnSkuData[]>([]);

  const [allowTableScroll, setAllowTableScroll] = useState<boolean>(false);
  const [scrollTop, setScrollTop] = useState<number>(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  const [returnData, setReturnData] = useState<any>();

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  useEffect(() => {
    let filtered = filterData(
      [...rows],
      searchedValue?.toLowerCase(),
      searchWithStatusFilter
    );

    setFilteredRows(filtered);
  }, [rows, searchedValue, searchWithStatusFilter]);

  const fetchInvoiceData = async () => {
    setLoader(true);
    try {
      const data = await getReturnAndReference(invoiceNumber);

      const retData = data.filter(
        (item: any) => item.type.toLowerCase() === "primaryreturn"
      )[0];
      const rowData = await generateViewReturnWithoutGRNRows(retData);

      const totalReturnValue = rowData.reduce(
        (acc, sku) => acc + (sku.totalReturnedValue || 0),
        0
      );
      const totalReturnedCaseQuantity = rowData.reduce(
        (acc, sku) => acc + (sku.totalReturnedCases || 0),
        0
      );
      const totalReturnedPieceQuantity = rowData.reduce(
        (acc, sku) => acc + (sku.totalReturnedPieces || 0),
        0
      );
      const totalReturnedOtherQuantity = rowData.reduce(
        (acc, sku) => acc + (sku.totalReturnedOtherUnits || 0),
        0
      );

      retData.totalReturnValue = totalReturnValue;
      retData.totalReturnedCaseQuantity = totalReturnedCaseQuantity;
      retData.totalReturnedPieceQuantity = totalReturnedPieceQuantity;
      retData.totalReturnedOtherQuantity = totalReturnedOtherQuantity;

      setReturnData(retData);

      setRows(rowData);
    } catch (error) {
      console.log(error);
      setGenericModalProps(
        getGenericModalProps("error", "Something went wrong.")
      );
    } finally {
      setLoader(false);
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setActiveRow({} as ViewReturnBatchDetailsWithoutGRN);
    setPopoverOpen(false);
  };

  useEffect(() => {
    fetchInvoiceData();
    handleClosePopover();
  }, [invoiceNumber]);

  const [activeRow, setActiveRow] = useState<ViewReturnBatchDetailsWithoutGRN>(
    {} as ViewReturnBatchDetailsWithoutGRN
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleClickInfoSku = (
    event: React.MouseEvent<HTMLElement>,
    rowData: ViewReturnSkuData
  ) => {
    setActiveRow(rowData.batches[0]);
    setAnchorEl(event.currentTarget);
    setPopoverOpen(true);
  };

  const handleClickInfoBatch = (
    event: React.MouseEvent<HTMLElement>,
    batchData: ViewReturnBatchDetailsWithoutGRN
  ) => {
    setActiveRow(batchData);
    setPopoverOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const columns: any[] = ViewPrimaryReturnWithoutGRNConfig(handleClickInfoSku).columns;
  const expandedRowColumns: any[] =
    ViewPrimaryReturnExpandedRowConfig(handleClickInfoBatch).columns;

  return (
    <div className="view-primary-order-container">
      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        rightButtonText={genericModalProps?.rightButtonText}
        modalOpen={genericModalProps?.open}
        onLeftButtonClick={genericModalProps?.onLeftButtonClick}
        onRightButtonClick={genericModalProps?.onRightButtonClick}
        leftButtonPadding={genericModalProps?.leftButtonPadding}
        onClose={() => setGenericModalProps(initialGenericModalProps)}
        width={genericModalProps?.width}
      />
      <ScreenOverlay apply={false} />
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => setPopoverOpen(false)} // replace with your close handler if different
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        TransitionComponent={Fade}
        transitionDuration={150}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "12px",
            boxShadow: "0px 2px 12px rgba(0,0,0,0.15)",
            minWidth: 330,
            maxWidth: 420,
            zIndex: 99999,
            // Use transform to mimic the small offset you had with Popper offset: [0,8]
            transform: "translateY(8px)",
          },
        }}
      >
        {/* Popover provides the Paper already via PaperProps; use the same inner content */}
        {activeRow && (
          <Box>
            {/* Rows */}
            <Box
              sx={{
                overflow: "auto",
                px: 0,
                flex: "1 1 auto",
                maxHeight: `150px`,
                scrollbarWidth: "none",
              }}
            >
              <List
                disablePadding
                sx={{ display: "flex", flexDirection: "column", gap: "5px" }}
              >
                {(() => {
                  // normalize source entries
                  const rows = (activeRow && activeRow.qtyReason) || [];
                  const filteredRows = rows.filter(
                    (row: any) =>
                      row.caseQty > 0 || row.pieceQty > 0 || row.otherQty > 0
                  );

                  if (rows.length === 0) {
                    return (
                      <ListItem sx={{ py: 2, px: 2 }}>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: "0.72rem" }}
                            >
                              No return quantity added
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  }

                  return rows.map((row, i: number) => {
                    return (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1,
                          borderBottom:
                            i !== rows.length - 1 ? "1px dashed #eee" : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            width: "min-content",
                            border: "1px solid #EEEEEE",
                            borderRadius: "4px",
                            padding: "8px",
                          }}
                        >
                          {caseUnitLabelPrimaryReturn && (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "2px",
                                  color: "rgb(124, 124, 124)",
                                }}
                              >
                                <span style={{ fontSize: 12, fontWeight: 500 }}>
                                  {row.caseQty}
                                </span>
                                <span style={{ fontSize: 12, color: "#999" }}>
                                  {caseUnitLabelPrimaryReturn}
                                </span>
                              </div>
                              <div
                                style={{
                                  width: "1px",
                                  height: 16,
                                  backgroundColor: "#E0E0E0",
                                }}
                              />
                            </>
                          )}
                          {otherUnitLabelPrimaryReturn && (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "2px",
                                  color: "rgb(124, 124, 124)",
                                }}
                              >
                                <span style={{ fontSize: 12, fontWeight: 500 }}>
                                  {row.otherQty}
                                </span>
                                <span style={{ fontSize: 12, color: "#999" }}>
                                  {activeRow?.otherUnitName ?? otherUnitLabelPrimaryReturn}
                                </span>
                              </div>
                              <div
                                style={{
                                  width: "1px",
                                  height: 16,
                                  backgroundColor: "#E0E0E0",
                                }}
                              />
                            </>
                          )}
                          {packUnitLabelPrimaryReturn && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                                color: "rgb(124, 124, 124)",
                              }}
                            >
                              <span style={{ fontSize: 12, fontWeight: 500 }}>
                                {row.pieceQty}
                              </span>
                              <span style={{ fontSize: 12, color: "#999" }}>
                                {packUnitLabelPrimaryReturn}
                              </span>
                            </div>
                          )}
                        </div>

                        <Box>
                          <Tooltip title={row.reason} arrow>
                            <Typography
                              fontSize="12px"
                              color="#666666"
                              fontWeight={500}
                              width="10rem"
                              sx={{
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                cursor: "default",
                                textAlign: "center",
                              }}
                            >
                              {row.reason}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </List>
            </Box>
          </Box>
        )}
      </Popover>
      <div className="view_order_header_with_search">
        <div className="view_order_header">
          <FormHeader
            parentLabel={"Primary Return"}
            label={"View Return"}
            goBackToParent={() => navigate(-1)}
          />
        </div>
        <div className="view_order_manage_order_search_bar">
          <SearchV2 searchFunction={handleSearchValueChange} />
        </div>
      </div>
      <div
        ref={scrollRef}
        style={{
          gap: "14px",
          overflow: "auto",
          height: "100%",
          scrollbarWidth: ConfigDictionary?.showTableScrollbar
            ? "thin"
            : "none",
        }}
        onScroll={(e) => {
          if (ConfigDictionary?.showTableScrollbar) {
            const currentScrollTop = e.currentTarget.scrollTop;
            if (currentScrollTop > scrollTop) {
              setIsScrolling(true); // scrolling down
            } else if (currentScrollTop < scrollTop) {
              setIsScrolling(false); // scrolling up
            }
          }
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            borderRadius: "10px",
            border: "1px solid #E7E7E7",
            padding: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              margin: "0px 5px",
            }}
          >
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              <span style={{ fontSize: "14px", fontWeight: 500 }}>
                <span style={{}}>{`Return Order No.`}</span>{" "}
                <span style={{}}>{`${returnData?.invoiceNumber}`}</span>
              </span>
            </div>
          </div>
          <div>
            <CustomAccordianV2
              metaData1={ViewPrimaryReturnWithoutGRNMetadata(currency, returnData)}
              data1={returnData}
              showMoreDetails={false}
            />
          </div>
        </div>
        <div style={{ height: "100%", position: "sticky", top: "0" }}>
          <div className="view-primary-order-details">
            <div
              className="view-primary-order-details-header"
              style={{ padding: "8px 0px" }}
            >
              <BorderHeading>View Return Details</BorderHeading>
            </div>
            {loader ? (
              <Loader />
            ) : (
              <CustomTablewithTotalInfoV2
                data={filteredRows}
                columns={columns}
                totals={[]}
                allowTableScroll={allowTableScroll}
                expandableRowColumns={expandedRowColumns}
                setScrollTop={setScrollTop}
                headerBackgroundColor="#f3f9fb"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPrimaryReturnWithoutGRN;
